import { SizableText, Stack, YStack } from '@mythical/ui'
import {
  BannerPattern,
  PATTERN_COLORS,
  type BannerPatternType,
} from 'app/components/blocks/Ad/BannerPattern'
import { ButtonLink } from 'app/components/primitive/Button'
import RichText from 'app/components/primitive/RichText'
import { Container, ContainerProps } from 'app/components/structure/Container'
import { useStores } from 'app/store'
import { ColorEnum, mapColor } from 'app/utils/mapColors'
import React from 'react'
import { SolitoImage } from 'solito/image'

export interface AdCustomColourProps extends ContainerProps {
  backgroundColour?: ColorEnum
  backgroundPattern?: BannerPatternType
  ctaLabel?: string
  description?: string
  foregroundColour?: ColorEnum
  href?: string
  image: string
  richTextDescription?: React.ReactNode
  title: string
}

export const AdCustomColour = ({
  backgroundColour,
  backgroundPattern,
  ctaLabel,
  description,
  foregroundColour,
  href,
  image,
  richTextDescription,
  title,
  ...props
}: AdCustomColourProps) => {
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  const hasPattern = backgroundPattern && backgroundPattern !== 'none'
  const bgColor = mapColor(backgroundColour)
  const fgColor = mapColor(foregroundColour)

  return (
    <Container paddedX variant="none" {...props}>
      <YStack
        backgroundColor={
          hasPattern ? PATTERN_COLORS[backgroundPattern].bg : bgColor
        }
        borderRadius="$2"
        overflow="hidden"
        position="relative"
        zIndex={0}
        $gtSm={{
          alignItems: 'center',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: 0,
          justifyContent: 'space-between',
          px: '$8.5',
          py: '$6',
        }}
        $gtMd={{
          px: '$6',
        }}
        $gtLg={{
          px: '$8.5',
        }}
      >
        <YStack
          overflow="hidden"
          p="$3.5"
          position="relative"
          width="100%"
          $gtSm={{ display: 'none' }}
        >
          {!!image && (
            <Stack aspectRatio={16 / 9} borderRadius="$2" overflow="hidden">
              <SolitoImage
                alt=""
                contentFit="cover"
                fill
                sizes="(min-width: 800px) 548px, 100vw"
                src={image}
              />
            </Stack>
          )}
        </YStack>

        <YStack
          fg={1}
          flexBasis="45%"
          fs={1}
          gap="$3.5"
          pb="$3.5"
          px="$3.5"
          $gtSm={{ p: 0, pr: '$8.5', gap: '$6' }}
        >
          <YStack gap="$2" $gtSm={{ gap: '$3.5' }}>
            <SizableText
              color={fgColor}
              ff="$display"
              tt="uppercase"
              $gtSm={{ size: '$l' }}
            >
              {title}
            </SizableText>
            {richTextDescription && (
              <RichText
                content={richTextDescription}
                textProps={{
                  color: fgColor,
                  size: '$s',
                  fontWeight: '500',
                  $gtSm: {
                    size: '$m',
                    fontWeight: '500',
                  },
                }}
              />
            )}
          </YStack>

          {canMemberfulLink && !!href && !!ctaLabel && (
            <ButtonLink
              alignSelf="flex-start"
              backgroundColor={fgColor}
              href={href}
              size="$4.5"
              textProps={{
                ff: '$title',
                fontWeight: '500',
              }}
              hoverStyle={{
                backgroundColor: fgColor,
                opacity: 0.9,
              }}
              focusStyle={{
                backgroundColor: fgColor,
                opacity: 0.8,
              }}
              pressStyle={{
                backgroundColor: fgColor,
                opacity: 0.7,
              }}
              {...(href?.startsWith('http') ? { target: '_blank' } : {})}
            >
              {ctaLabel}
            </ButtonLink>
          )}
        </YStack>

        {!!image && (
          <Stack
            aspectRatio={16 / 9}
            borderRadius="$2"
            display="none"
            fg={1}
            flexBasis="55%"
            fs={1}
            overflow="hidden"
            $gtSm={{
              display: 'flex',
            }}
          >
            <SolitoImage
              alt=""
              contentFit="cover"
              fill
              sizes="(min-width: 800px) 548px, 100vw"
              src={image}
            />
          </Stack>
        )}

        {hasPattern && <BannerPattern backgroundPattern={backgroundPattern} />}
      </YStack>
    </Container>
  )
}
